import BaseModel from './BaseModel'
import { ChannelFacility } from './ChannelFacility'
import { DefaultRate } from './DefaultRate'
// import { Inventory } from './Inventory'
import Location from './Location'
import Parcs from './Parcs'
import Product from './Product'
import SpotHeroFlexDiscount from './SpotHeroFlexDiscount'
import { VenueLocationProduct } from './VenueLocationProduct'
import handleAxiosError from '../utils/handleAxiosError'

export default class LocationProduct extends BaseModel {

  static entity = 'location_products'
  static primaryKey = 'ID'

  declare ID: number
  declare locationID: number | null
  declare productID: number | null
  declare earlyBird: boolean
  declare inOut: boolean
  declare parcsID: number | null
  declare entryHoursBeforeEvent: number | null
  declare exitHoursAfterEvent: number | null
  declare firstStartTime: string | null
  declare lastStartTime: string | null
  declare endTime: string | null
  declare channelFacilityIDs: number[] // deprecated - use locationProductChannelFacilities instead
  declare sendRatesForChannelFacilityIDs: number[] // deprecated - use locationProductChannelFacilities instead
  declare channelFacilities: ChannelFacility[]
  declare location: Location
  declare product: Product
  declare parcs: Parcs
  declare spotheroFlexDiscounts: SpotHeroFlexDiscount[]
  declare defaultRates: DefaultRate[]
  declare venueLocationProducts: VenueLocationProduct[]
  // declare inventories: Inventory[]

  static fields() {
    return {

      // fields
      ID: this.number(null).nullable(), // this is not returned by the API
      locationID: this.number(null).nullable(), // this is not returned by the API; we must infer it from context
      productID: this.number(null).nullable(),
      earlyBird: this.boolean(null),
      inOut: this.boolean(null),
      parcsID: this.number(null).nullable(),
      entryHoursBeforeEvent: this.number(null).nullable(),
      exitHoursAfterEvent: this.number(null).nullable(),
      firstStartTime: this.string(null).nullable(),
      lastStartTime: this.string(null).nullable(),
      endTime: this.string(null).nullable(),
      channelFacilityIDs: this.attr([]), // this is named "channelFacilities" in the API response; we rename it in the API methods below
      sendRatesForChannelFacilityIDs: this.attr([]),

      // relationships
      channelFacilities: this.hasManyBy(ChannelFacility, 'channelFacilityIDs'), // TODO: this doesn't seem to work
      // inventories: this.hasMany(Inventory, 'inventories'),
      location: this.belongsTo(Location, 'locationID'),
      product: this.belongsTo(Product, 'productID'),
      parcs: this.belongsTo(Parcs, 'parcsID'),
      spotheroFlexDiscounts: this.hasMany(SpotHeroFlexDiscount, 'locationProductID'),
      defaultRates: this.hasMany(DefaultRate, 'locationProductID'),
      venueLocationProducts: this.hasMany(VenueLocationProduct, 'locationProductID'),

    }
  }

  /**
   * API methods
   */

  static fetch(locationID: number, locationProductID: number) {

    return this.api()
      .get('/locations/' + locationID + '/location_products/' + locationProductID, {
        dataKey: 'locationProduct',
      })
      .catch(handleAxiosError)

  }

  static fetchByLocationID(locationID: number) {

    return this.api()
      .get('/locations/' + locationID + '/location_products', {
        dataTransformer: ({ data }) => {

          // In addition to returning LocationProducts, this endpoint also
          // returns ChannelFacilities, as kind of a side effect.
          // We'll store those here.
          LocationProduct._storeChannelFacilities(data.channelFacilities)

          const locationProducts = LocationProduct._massageRecordsFromAPI(data.locationProducts, locationID)
          return locationProducts

        },
      })
      .catch(handleAxiosError)

  }

  static saveNew(locationID: number, payload: object) {
    const url = '/locations/' + locationID + '/location_products'
    return this.api().post(url, payload).catch(handleAxiosError)
  }

  static saveExisting(locationID: number, locationProductID: number, payload: object) {
    const url = '/locations/' + locationID + '/location_products/' + locationProductID
    return this.api().put(url, payload).catch(handleAxiosError)
  }

  static destroy(locationID: number, locationProductID: number) {
    const url = '/locations/' + locationID + '/location_products/' + locationProductID
    return this.api()
      .delete(url, {
        delete: locationProductID,
      })
      .catch(handleAxiosError)
  }

  static _massageRecordsFromAPI(locationProducts: any[], locationID: number) {

    return locationProducts.map((locationProduct) => {
          
      // The API doesn't include the locationID in the returned
      // LocationProduct records. Append it manually.
      locationProduct.locationID = locationID

      // The API returns the list of ChannelFacility IDs in an array
      // named "channelFacilities", but that collides with the name of the
      // model relationship. Adjust

      locationProduct.channelFacilityIDs = locationProduct.channelFacilities
      delete locationProduct.channelFacilities

      return locationProduct

    })

  }

  static _storeChannelFacilities(channelFacilities: object[]) {

    channelFacilities.forEach((channelFacility) => {
      ChannelFacility.insert({
        data: channelFacility,
      })
    })

  }

}
